import { createFileRoute, Link } from "@tanstack/react-router";
import type { CompanyKind } from "@alm/validation";
import { PageHeader } from "@/components/app/shell/page-header";
import { Button } from "@/components/ui/button";
import { useFacility } from "@/lib/facility/facility";
import { ArrowLeft } from "@/lib/icons";
import { guardRoute } from "@/lib/policy/route-guard";
import { VisitHistory, type HistorySource } from "../-components/visit-history";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/companies/$companyKey")({
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: VisitorCompanyPage,
});

/** `institution:<id>` and `pharmacy:<id>` filter by the directory row; `text:<name>` by the typed
 * name. */
function sourceFor(key: string, facilityId: string): { source: HistorySource; title: string } {
  const [kind = "", ...rest] = key.split(":");
  const value = rest.join(":");
  if (kind === "institution" || kind === "pharmacy") {
    return {
      source: {
        kind: "list",
        filter: { facilityId, companyKind: kind as CompanyKind, companyId: value },
      },
      title: "Visits from this company",
    };
  }
  return {
    source: { kind: "list", filter: { facilityId, search: value } },
    title: `Visits typed as “${value}”`,
  };
}

/** Everyone who came from one company: the history behind one row of the companies list. */
function VisitorCompanyPage() {
  const { companyKey } = Route.useParams();
  const { facilityId } = useFacility();
  const { source, title } = sourceFor(companyKey, facilityId);
  return (
    <div className="flex flex-col gap-5">
      <Button asChild variant="ghost" size="sm" className="self-start">
        <Link to="/visitors/companies">
          <ArrowLeft data-icon="inline-start" aria-hidden="true" />
          All companies
        </Link>
      </Button>
      <PageHeader title={title} />
      <VisitHistory source={source} emptyTitle="No visits from this company" />
    </div>
  );
}
